/* ============================================================
   Orange Spot Noir — arcade.js
   „Spot Invaders“: eine Space-Invaders-Hommage für das Ende der Startseite.
   Vanilla ES2020, keine Abhängigkeiten. Logische Auflösung 224×256 wie das
   Original, ganzzahlig auf den Automaten-Bildschirm skaliert (scharfe Pixel),
   feste Schrittweite 60 Hz, Sprites und 5×7-Schrift als Pixelmasken (je Farbe
   vorgerendert), zerstörbare Bunker als Bitmasken, Bestenliste im Browser,
   Rabattcode je Punktestufe (Stufen kommen aus den Sektionseinstellungen).
   Bedienung: Pfeiltasten/A D, Leertaste, P = Pause, Enter = Start; am Handy
   Tasten unter dem Bildschirm oder Finger auf dem Bildschirm (ziehen + tippen).
   ============================================================ */
(function () {
  'use strict';

  const cab = document.querySelector('[data-arcade]');
  if (!cab) return;
  const canvas = cab.querySelector('.cab__canvas');
  if (!canvas || !canvas.getContext) return;

  /* ---------- Konstanten ---------- */
  const W = 224, H = 256, STEP = 1000 / 60;
  const COLOR = { fg: '#f2f0ee', orange: '#fd7c2b', peach: '#ffb27a', dim: '#8d8b8a', ok: '#4ade80', red: '#f87171', bunker: '#d7651f', bg: '#050506' };
  const COLS = 11, ROWS = 5, CELL = 16;
  const ROW_TYPE = [0, 1, 1, 2, 2];           // Reihe 0 (oben): Tintenfisch, dann 2× Krabbe, 2× Krake
  const POINTS = [30, 20, 10];
  const UFO_POINTS = [50, 50, 50, 50, 100, 100, 100, 150, 150, 300];
  const PLAYER_Y = H - 30, PLAYER_SPEED = 78, SHOT_SPEED = 250, EXTRA_LIFE_AT = 1500;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rand = (a, b) => a + Math.random() * (b - a);
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const pad = (n, l = 5) => String(Math.max(0, Math.floor(n))).padStart(l, '0');

  /* ---------- Pixelmasken: Sprites ---------- */
  let spriteId = 0;
  const parse = (rows) => ({ id: spriteId++, w: rows[0].length, h: rows.length, rows });
  const SPR = {
    squid: [parse(['...##...', '..####..', '.######.', '##.##.##', '########', '..#..#..', '.#.##.#.', '#.#..#.#']),
            parse(['...##...', '..####..', '.######.', '##.##.##', '########', '.#....#.', '#......#', '.#....#.'])],
    crab:  [parse(['..#.....#..', '...#...#...', '..#######..', '.##.###.##.', '###########', '#.#######.#', '#.#.....#.#', '...##.##...']),
            parse(['..#.....#..', '#..#...#..#', '#.#######.#', '###.###.###', '###########', '.#########.', '..#.....#..', '.#.......#.'])],
    octo:  [parse(['....####....', '.##########.', '############', '###..##..###', '############', '...##..##...', '..##.##.##..', '##........##']),
            parse(['....####....', '.##########.', '############', '###..##..###', '############', '..###..###..', '.##..##..##.', '..##....##..'])],
    player: parse(['......#......', '.....###.....', '.....###.....', '.###########.', '#############', '#############', '#############', '#############']),
    playerDot: parse(['.....###.....', '.....###.....']),
    ufo: parse(['.....######.....', '...##########...', '..############..', '.##.##.##.##.##.', '################', '...###....###...', '....#......#....']),
    boom: parse(['....#..#.....', '.#...##...#..', '..#......#...', '...#....#....', '##..........##', '...#....#....', '..#..##..#...', '.#..#..#..#..']),
    bolt: [parse(['.#.', '#..', '.#.', '..#', '.#.', '#..', '.#.']), parse(['#..', '.#.', '..#', '.#.', '#..', '.#.', '..#'])],
    shot: parse(['#', '#', '#', '#']),
    life: parse(['....#....', '...###...', '#########', '#########', '#########']),
  };
  const INV = [SPR.squid, SPR.crab, SPR.octo];
  const BUNKER = parse([
    '....##############....', '...################...', '..##################..', '.####################.',
    '######################', '######################', '######################', '######################',
    '######################', '######################', '######################', '######################',
    '#######........#######', '######..........######', '#####............#####', '#####............#####',
  ]);

  /* ---------- 5×7-Bitmap-Schrift (Großbuchstaben, Ziffern, Zeichen, Umlaute) ---------- */
  const FONT_HEX = {
    A: [0x0E, 0x11, 0x11, 0x1F, 0x11, 0x11, 0x11], B: [0x1E, 0x11, 0x11, 0x1E, 0x11, 0x11, 0x1E], C: [0x0E, 0x11, 0x10, 0x10, 0x10, 0x11, 0x0E],
    D: [0x1E, 0x11, 0x11, 0x11, 0x11, 0x11, 0x1E], E: [0x1F, 0x10, 0x10, 0x1E, 0x10, 0x10, 0x1F], F: [0x1F, 0x10, 0x10, 0x1E, 0x10, 0x10, 0x10],
    G: [0x0E, 0x11, 0x10, 0x17, 0x11, 0x11, 0x0F], H: [0x11, 0x11, 0x11, 0x1F, 0x11, 0x11, 0x11], I: [0x0E, 0x04, 0x04, 0x04, 0x04, 0x04, 0x0E],
    J: [0x07, 0x02, 0x02, 0x02, 0x02, 0x12, 0x0C], K: [0x11, 0x12, 0x14, 0x18, 0x14, 0x12, 0x11], L: [0x10, 0x10, 0x10, 0x10, 0x10, 0x10, 0x1F],
    M: [0x11, 0x1B, 0x15, 0x15, 0x11, 0x11, 0x11], N: [0x11, 0x11, 0x19, 0x15, 0x13, 0x11, 0x11], O: [0x0E, 0x11, 0x11, 0x11, 0x11, 0x11, 0x0E],
    P: [0x1E, 0x11, 0x11, 0x1E, 0x10, 0x10, 0x10], Q: [0x0E, 0x11, 0x11, 0x11, 0x15, 0x12, 0x0D], R: [0x1E, 0x11, 0x11, 0x1E, 0x14, 0x12, 0x11],
    S: [0x0F, 0x10, 0x10, 0x0E, 0x01, 0x01, 0x1E], T: [0x1F, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04], U: [0x11, 0x11, 0x11, 0x11, 0x11, 0x11, 0x0E],
    V: [0x11, 0x11, 0x11, 0x11, 0x11, 0x0A, 0x04], W: [0x11, 0x11, 0x11, 0x15, 0x15, 0x15, 0x0A], X: [0x11, 0x11, 0x0A, 0x04, 0x0A, 0x11, 0x11],
    Y: [0x11, 0x11, 0x11, 0x0A, 0x04, 0x04, 0x04], Z: [0x1F, 0x01, 0x02, 0x04, 0x08, 0x10, 0x1F],
    'Ä': [0x0A, 0x00, 0x0E, 0x11, 0x1F, 0x11, 0x11], 'Ö': [0x0A, 0x00, 0x0E, 0x11, 0x11, 0x11, 0x0E], 'Ü': [0x0A, 0x00, 0x11, 0x11, 0x11, 0x11, 0x0E],
    0: [0x0E, 0x11, 0x13, 0x15, 0x19, 0x11, 0x0E], 1: [0x04, 0x0C, 0x04, 0x04, 0x04, 0x04, 0x0E], 2: [0x0E, 0x11, 0x01, 0x02, 0x04, 0x08, 0x1F],
    3: [0x1F, 0x02, 0x04, 0x02, 0x01, 0x11, 0x0E], 4: [0x02, 0x06, 0x0A, 0x12, 0x1F, 0x02, 0x02], 5: [0x1F, 0x10, 0x1E, 0x01, 0x01, 0x11, 0x0E],
    6: [0x06, 0x08, 0x10, 0x1E, 0x11, 0x11, 0x0E], 7: [0x1F, 0x01, 0x02, 0x04, 0x08, 0x08, 0x08], 8: [0x0E, 0x11, 0x11, 0x0E, 0x11, 0x11, 0x0E],
    9: [0x0E, 0x11, 0x11, 0x0F, 0x01, 0x02, 0x0C],
    ' ': [0, 0, 0, 0, 0, 0, 0], '-': [0, 0, 0, 0x1F, 0, 0, 0], '.': [0, 0, 0, 0, 0, 0x0C, 0x0C], ':': [0, 0x0C, 0x0C, 0, 0x0C, 0x0C, 0],
    '=': [0, 0, 0x1F, 0, 0x1F, 0, 0], '?': [0x0E, 0x11, 0x01, 0x02, 0x04, 0, 0x04], '!': [0x04, 0x04, 0x04, 0x04, 0x04, 0, 0x04],
    '<': [0x02, 0x04, 0x08, 0x10, 0x08, 0x04, 0x02], '>': [0x08, 0x04, 0x02, 0x01, 0x02, 0x04, 0x08], '+': [0, 0x04, 0x04, 0x1F, 0x04, 0x04, 0],
    '/': [0x01, 0x01, 0x02, 0x04, 0x08, 0x10, 0x10], '€': [0x06, 0x09, 0x1C, 0x08, 0x1C, 0x09, 0x06], '*': [0, 0x15, 0x0E, 0x1F, 0x0E, 0x15, 0],
  };
  const GLYPH = {};
  Object.keys(FONT_HEX).forEach((ch) => { GLYPH[ch] = parse(FONT_HEX[ch].map((b) => [16, 8, 4, 2, 1].map((m) => (b & m ? '#' : '.')).join(''))); });

  /* ---------- Vorgerenderte Stempel je Sprite und Farbe ---------- */
  const stamps = new Map();
  function stamp(s, color) {
    const key = s.id + '|' + color;
    let c = stamps.get(key);
    if (!c) {
      c = document.createElement('canvas'); c.width = s.w; c.height = s.h;
      const g = c.getContext('2d'); g.fillStyle = color;
      s.rows.forEach((row, y) => { for (let x = 0; x < row.length; x++) if (row[x] === '#') g.fillRect(x, y, 1, 1); });
      stamps.set(key, c);
    }
    return c;
  }
  const draw = (g, s, x, y, color) => g.drawImage(stamp(s, color), Math.round(x), Math.round(y));
  function text(g, str, x, y, color, scale, align) {
    scale = scale || 1;
    const cw = 6 * scale, w = str.length * cw - scale;
    let px = align === 'center' ? Math.round(x - w / 2) : align === 'right' ? Math.round(x - w) : Math.round(x);
    for (const ch of String(str).toUpperCase()) {
      const gl = GLYPH[ch];
      if (gl) g.drawImage(stamp(gl, color), px, Math.round(y), 5 * scale, 7 * scale);
      px += cw;
    }
  }

  /* ---------- Bunker: zerstörbare Bitmaske ---------- */
  class Bunker {
    constructor(x, y) {
      this.x = x; this.y = y; this.w = BUNKER.w; this.h = BUNKER.h;
      this.mask = new Uint8Array(this.w * this.h);
      BUNKER.rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) this.mask[j * this.w + i] = row[i] === '#' ? 1 : 0; });
      this.canvas = document.createElement('canvas'); this.canvas.width = this.w; this.canvas.height = this.h;
      this.dirty = true;
    }
    solid(px, py) {
      const i = Math.floor(px - this.x), j = Math.floor(py - this.y);
      return i >= 0 && j >= 0 && i < this.w && j < this.h && this.mask[j * this.w + i] === 1;
    }
    damage(px, py, r) {
      const cx = px - this.x, cy = py - this.y;
      for (let j = -r; j <= r; j++) for (let i = -r; i <= r; i++) {
        if (i * i + j * j > r * r + 1) continue;
        const x = Math.round(cx + i), y = Math.round(cy + j);
        if (x >= 0 && y >= 0 && x < this.w && y < this.h && Math.random() < 0.88) this.mask[y * this.w + x] = 0;
      }
      this.dirty = true;
    }
    clearRect(x0, y0, x1, y1) {
      const a = clamp(Math.floor(x0 - this.x), 0, this.w), b = clamp(Math.ceil(x1 - this.x), 0, this.w);
      const c = clamp(Math.floor(y0 - this.y), 0, this.h), d = clamp(Math.ceil(y1 - this.y), 0, this.h);
      for (let j = c; j < d; j++) for (let i = a; i < b; i++) this.mask[j * this.w + i] = 0;
      this.dirty = true;
    }
    render(g) {
      if (this.dirty) {
        const c = this.canvas.getContext('2d'); c.clearRect(0, 0, this.w, this.h); c.fillStyle = COLOR.bunker;
        for (let j = 0; j < this.h; j++) for (let i = 0; i < this.w; i++) if (this.mask[j * this.w + i]) c.fillRect(i, j, 1, 1);
        this.dirty = false;
      }
      g.drawImage(this.canvas, this.x, this.y);
    }
  }

  /* ---------- Ton (WebAudio, erst nach Klick, standardmäßig aus) ---------- */
  class Sound {
    constructor(on) { this.on = on; this.ctx = null; }
    ensure() {
      if (!this.ctx) { const AC = window.AudioContext || window.webkitAudioContext; if (AC) this.ctx = new AC(); }
      if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
      return this.ctx;
    }
    tone(freq, dur, o) {
      if (!this.on) return; const c = this.ensure(); if (!c) return; o = o || {};
      const osc = c.createOscillator(), g = c.createGain(), t = c.currentTime;
      osc.type = o.type || 'square'; osc.frequency.setValueAtTime(freq, t);
      if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t + dur);
      g.gain.setValueAtTime(o.vol || 0.05, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(g).connect(c.destination); osc.start(t); osc.stop(t + dur + 0.02);
    }
    noise(dur, vol) {
      if (!this.on) return; const c = this.ensure(); if (!c) return;
      const n = Math.floor(c.sampleRate * dur), b = c.createBuffer(1, n, c.sampleRate), d = b.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
      const src = c.createBufferSource(), g = c.createGain(); src.buffer = b; g.gain.value = vol || 0.08;
      src.connect(g).connect(c.destination); src.start();
    }
    shoot() { this.tone(880, 0.12, { to: 220, vol: 0.045 }); }
    step(i) { this.tone([110, 98, 87, 78][i & 3], 0.08, { type: 'triangle', vol: 0.09 }); }
    hit() { this.noise(0.1, 0.06); }
    ufo() { this.tone(500, 0.16, { to: 720, type: 'sine', vol: 0.03 }); }
    die() { this.noise(0.45, 0.1); this.tone(320, 0.6, { to: 40, type: 'sawtooth', vol: 0.05 }); }
    bonus() { const t = this; t.tone(660, 0.08); setTimeout(() => t.tone(880, 0.1), 90); setTimeout(() => t.tone(1320, 0.18), 190); }
  }

  /* ---------- Bestenliste (localStorage, zehn Plätze; drei Hausrekorde als Start) ---------- */
  const Scores = {
    key: 'osn.arcade.scores',
    seed: [{ n: 'NVA', s: 4200, w: 4 }, { n: 'SPT', s: 2600, w: 3 }, { n: 'HOF', s: 1200, w: 2 }],
    load() { try { const a = JSON.parse(localStorage.getItem(this.key) || 'null'); if (Array.isArray(a) && a.length) return a; } catch (e) { /* kein Speicher */ } return null; },
    save(a) { try { localStorage.setItem(this.key, JSON.stringify(a.slice(0, 10))); } catch (e) { /* privat/voll */ } },
    list() { return this.load() || this.seed.slice(); },
    hi() { return this.list().reduce((m, e) => Math.max(m, e.s | 0), 0); },
    qualifies(s) { const l = this.list(); return s > 0 && (l.length < 10 || s > l[l.length - 1].s); },
    add(n, s, w) {
      const entry = { n, s, w, d: new Date().toISOString().slice(0, 10) };
      const l = this.list().concat([entry]).sort((a, b) => b.s - a.s).slice(0, 10);
      this.save(l); return { list: l, index: l.indexOf(entry) };
    },
  };

  /* ---------- Rabattstufen aus der Sektion ---------- */
  let TIERS = [];
  try { TIERS = JSON.parse(cab.dataset.tiers || '[]'); } catch (e) { TIERS = []; }
  TIERS = TIERS.filter((t) => t && t.code).map((t) => ({ min: Number(t.min) || 0, code: String(t.enc ? atob(t.code) : t.code), value: String(t.value || '') })).sort((a, b) => a.min - b.min);
  const tierFor = (score) => TIERS.reduce((best, t) => (score >= t.min ? t : best), TIERS[0] || null);

  /* ---------- Oberfläche (DOM-Overlays, Tasten, Tabelle) ---------- */
  const section = cab.closest('section') || document;
  const $ = (s, c) => (c || cab).querySelector(s);
  const $$ = (s, c) => Array.from((c || cab).querySelectorAll(s));
  const ui = {
    screens: { start: $('[data-arcade-screen="start"]'), pause: $('[data-arcade-screen="pause"]'), over: $('[data-arcade-screen="over"]') },
    hi: $('[data-arcade-hi]'), final: $('[data-arcade-final]'), wave: $('[data-arcade-wave]'),
    form: $('[data-arcade-form]'), initials: $('[data-arcade-initials]'),
    prizeValue: $('[data-arcade-prize-value]'), code: $('[data-arcade-code]'), copy: $('[data-arcade-copy]'), apply: $('[data-arcade-apply]'),
    scores: section.querySelector('[data-arcade-scores]'), soundBtn: $('[data-arcade-sound]'),
    show(name) { Object.keys(this.screens).forEach((k) => { const el = this.screens[k]; if (el) el.hidden = k !== name; }); },
    renderScores(highlight) {
      const list = Scores.list();
      if (this.hi) this.hi.textContent = pad(Scores.hi(), 5);
      if (!this.scores) return;
      this.scores.innerHTML = list.map((e, i) => `<li${i === highlight ? ' class="is-new"' : ''}><span class="rk">${String(i + 1).padStart(2, '0')}</span><span class="nm">${escapeHtml(e.n)}</span><span class="sc">${pad(e.s, 5)}</span><span class="wv">W${e.w | 0}</span></li>`).join('');
    },
  };
  /* v1.8: Texte aus window.OSN_I18N (layout/theme.liquid), zweiter Wert = deutscher Rückfalltext */
  const I18N = window.OSN_I18N || {};
  const T = (k, fb) => (I18N[k] != null ? String(I18N[k]) : fb);
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));

  let soundOn = false;
  try { const v = localStorage.getItem('osn.arcade.sound'); soundOn = v === null ? cab.dataset.sound === 'true' : v === '1'; } catch (e) { soundOn = false; }
  const sound = new Sound(soundOn);
  function paintSound() { if (!ui.soundBtn) return; ui.soundBtn.setAttribute('aria-pressed', String(sound.on)); ui.soundBtn.classList.toggle('is-on', sound.on); const l = $('b', ui.soundBtn); if (l) l.textContent = sound.on ? T('arc_sound_on', 'an') : T('arc_sound_off', 'aus'); }
  paintSound();
  if (ui.soundBtn) ui.soundBtn.addEventListener('click', () => { sound.on = !sound.on; if (sound.on) { sound.ensure(); sound.tone(660, 0.08); } try { localStorage.setItem('osn.arcade.sound', sound.on ? '1' : '0'); } catch (e) { /* egal */ } paintSound(); });

  /* ---------- Eingabe ---------- */
  const input = { left: false, right: false, fire: false, target: null };

  /* ---------- Das Spiel ---------- */
  class Game {
    constructor() {
      this.buf = document.createElement('canvas'); this.buf.width = W; this.buf.height = H;
      this.g = this.buf.getContext('2d');
      this.ctx = canvas.getContext('2d');
      this.hi = Scores.hi();
      this.t = 0; this.state = 'attract'; this.stateT = 0; this.prevState = null;
      this.score = 0; this.lives = 3; this.wave = 1; this.extraGiven = false;
      this.inv = []; this.bullets = []; this.shot = null; this.bunkers = []; this.ufo = null; this.ufoT = 0;
      this.particles = []; this.booms = []; this.floats = [];
      this.player = { x: W / 2 - 6, cool: 0 };
      this.dir = 1; this.stepT = 0; this.stepIdx = 0; this.frame = 0; this.fireT = 0; this.fy = 0; this.fx = 0;
      this.demo = { x: -60, frame: 0, t: 0 };
    }

    /* --- Zustände --- */
    setState(s, keep) { if (!keep) this.prevState = this.state; this.state = s; this.stateT = 0; }
    start() {
      this.score = 0; this.lives = 3; this.wave = 1; this.extraGiven = false;
      this.particles = []; this.booms = []; this.floats = [];
      this.bunkers = [0, 1, 2, 3].map((i) => new Bunker(31 + i * 46, H - 62));
      this.player.x = W / 2 - 6;
      this.newWave();
      this.readyText = 'BEREIT?';
      ui.show(null);
      this.setState('ready');
      try { canvas.focus({ preventScroll: true }); } catch (e) { canvas.focus(); }
    }
    newWave() {
      this.inv = [];
      this.fx = 24; this.fy = 40 + Math.min(this.wave - 1, 6) * 8; this.dir = 1; this.frame = 0;
      for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) this.inv.push({ t: ROW_TYPE[r], row: r, col: c, alive: true });
      this.bullets = []; this.shot = null; this.ufo = null; this.ufoT = rand(14000, 24000);
      this.stepT = 0; this.fireT = this.fireInterval();
    }
    pause() { if (this.state === 'play' || this.state === 'ready') { this.setState('paused'); ui.show('pause'); } }
    resume() { if (this.state === 'paused') { this.state = this.prevState || 'play'; this.stateT = 0; ui.show(null); try { canvas.focus({ preventScroll: true }); } catch (e) { /* egal */ } } }
    togglePause() { if (this.state === 'paused') this.resume(); else this.pause(); }
    gameOver() {
      this.setState('over');
      if (this.score > this.hi) this.hi = this.score;
      if (ui.hi) ui.hi.textContent = pad(this.hi, 5);
      if (ui.final) ui.final.textContent = pad(this.score, 5);
      if (ui.wave) ui.wave.textContent = String(this.wave);
      const tier = tierFor(this.score);
      if (tier) {
        if (ui.prizeValue) ui.prizeValue.textContent = tier.value;
        if (ui.code) ui.code.textContent = tier.code;
        if (ui.apply) ui.apply.href = '/discount/' + encodeURIComponent(tier.code) + '?redirect=' + encodeURIComponent(cab.dataset.apply || '/collections/all');
      }
      if (ui.form) { const q = Scores.qualifies(this.score); ui.form.hidden = !q; if (q && ui.initials) { ui.initials.value = ''; setTimeout(() => ui.initials.focus({ preventScroll: true }), 50); } }
      ui.show('over');
    }

    /* --- Schwierigkeit --- */
    speedMul() { return Math.max(0.5, 1 - (this.wave - 1) * 0.08); }
    stepInterval() { const n = this.inv.reduce((k, i) => k + (i.alive ? 1 : 0), 0); return Math.max(18, (50 + 650 * Math.pow(n / 55, 1.35)) * this.speedMul()); }
    fireInterval() { return rand(1, 1.7) * Math.max(320, 1200 - (this.wave - 1) * 110); }
    maxBolts() { return Math.min(1 + Math.floor((this.wave + 1) / 2), 4); }
    boltSpeed() { return Math.min(150, 70 + (this.wave - 1) * 9); }

    /* --- Geometrie --- */
    invX(i) { return this.fx + i.col * CELL + ((CELL - INV[i.t][0].w) >> 1); }
    invY(i) { return this.fy + i.row * CELL; }
    invW(i) { return INV[i.t][0].w; }
    alive() { return this.inv.filter((i) => i.alive); }

    /* --- Punkte --- */
    addScore(n) {
      this.score += n;
      if (!this.extraGiven && this.score >= EXTRA_LIFE_AT) { this.extraGiven = true; this.lives = Math.min(5, this.lives + 1); this.floats.push({ x: W / 2, y: H - 46, text: '+1 LEBEN', color: COLOR.ok, t: 1400 }); sound.bonus(); }
      if (this.score > this.hi) this.hi = this.score;
    }
    burst(x, y, color, n) {
      if (reduced) return;
      for (let i = 0; i < n; i++) this.particles.push({ x, y, vx: rand(-45, 45), vy: rand(-55, 25), t: rand(250, 550), color: Math.random() < 0.6 ? color : COLOR.fg });
    }

    /* --- Schleife --- */
    update(dt) {
      this.t += dt; this.stateT += dt;
      this.demo.t += dt; if (this.demo.t > 480) { this.demo.t = 0; this.demo.frame ^= 1; this.demo.x += 4; if (this.demo.x > W + 20) this.demo.x = -70; }
      this.particles.forEach((p) => { p.t -= dt; p.x += p.vx * dt / 1000; p.y += p.vy * dt / 1000; p.vy += 60 * dt / 1000; });
      this.particles = this.particles.filter((p) => p.t > 0);
      this.booms.forEach((b) => { b.t -= dt; }); this.booms = this.booms.filter((b) => b.t > 0);
      this.floats.forEach((f) => { f.t -= dt; f.y -= 8 * dt / 1000; }); this.floats = this.floats.filter((f) => f.t > 0);

      switch (this.state) {
        case 'ready': if (this.stateT > 1100) this.setState('play'); break;
        case 'play': this.updatePlay(dt); break;
        case 'dying':
          if (this.stateT > 1300) {
            this.lives--;
            if (this.lives <= 0) { this.gameOver(); }
            else { this.player.x = W / 2 - 6; this.bullets = []; this.shot = null; this.readyText = 'BEREIT?'; this.setState('ready'); }
          }
          break;
        case 'clear': if (this.stateT > 1500) { this.wave++; this.newWave(); this.readyText = 'WELLE ' + this.wave; this.setState('ready'); } break;
        default: break;
      }
    }

    updatePlay(dt) {
      const s = dt / 1000, p = this.player;
      /* Spieler */
      let vx = 0;
      if (input.target !== null) { const d = input.target - (p.x + 6); vx = Math.abs(d) < 1.5 ? 0 : Math.sign(d) * PLAYER_SPEED; }
      else vx = (input.right ? PLAYER_SPEED : 0) - (input.left ? PLAYER_SPEED : 0);
      p.x = clamp(p.x + vx * s, 4, W - 4 - SPR.player.w);
      p.cool = Math.max(0, p.cool - dt);
      if (input.fire && !this.shot && p.cool <= 0) { this.shot = { x: p.x + 6, y: PLAYER_Y - 4 }; p.cool = 140; sound.shoot(); }

      /* Formation */
      this.stepT += dt;
      const interval = this.stepInterval();
      if (this.stepT >= interval) {
        this.stepT -= interval;
        const alive = this.alive();
        let minX = W, maxX = 0;
        alive.forEach((i) => { minX = Math.min(minX, this.invX(i)); maxX = Math.max(maxX, this.invX(i) + this.invW(i)); });
        if ((this.dir > 0 && maxX + 2 > W - 8) || (this.dir < 0 && minX - 2 < 8)) { this.dir *= -1; this.fy += 8; }
        else this.fx += this.dir * 2;
        this.frame ^= 1; this.stepIdx++;
        if (alive.length) sound.step(this.stepIdx);
        /* Bunker unter der Formation zerbröseln, Landung = verloren */
        alive.forEach((i) => {
          const x = this.invX(i), y = this.invY(i), w = this.invW(i);
          this.bunkers.forEach((b) => { if (y + 8 > b.y && y < b.y + b.h && x + w > b.x && x < b.x + b.w) b.clearRect(x, y, x + w, y + 8); });
          if (y + 8 >= PLAYER_Y) this.invaded = true;
        });
        if (this.invaded) { this.invaded = false; this.killPlayer(true); return; }
      }

      /* Gegnerfeuer */
      this.fireT -= dt;
      if (this.fireT <= 0) {
        this.fireT = this.fireInterval();
        if (this.bullets.length < this.maxBolts()) {
          const cols = {}; this.alive().forEach((i) => { if (!cols[i.col] || i.row > cols[i.col].row) cols[i.col] = i; });
          const keys = Object.keys(cols);
          if (keys.length) { const sh = cols[keys[Math.floor(Math.random() * keys.length)]]; this.bullets.push({ x: this.invX(sh) + (this.invW(sh) >> 1) - 1, y: this.invY(sh) + 8, f: 0 }); }
        }
      }

      /* Spielerschuss */
      if (this.shot) {
        const sh = this.shot; sh.y -= SHOT_SPEED * s;
        let hit = false;
        if (sh.y < 8) hit = true;
        if (!hit) for (const b of this.bunkers) { if (b.solid(sh.x, sh.y)) { b.damage(sh.x, sh.y, 2); hit = true; break; } }
        if (!hit) for (const i of this.inv) {
          if (!i.alive) continue;
          const x = this.invX(i), y = this.invY(i), w = this.invW(i);
          if (sh.x >= x && sh.x <= x + w && sh.y <= y + 8 && sh.y + 4 >= y) {
            i.alive = false; hit = true; this.addScore(POINTS[i.t]); this.booms.push({ x: x + (w >> 1) - 6, y, t: 220 }); this.burst(x + w / 2, y + 4, COLOR.orange, 6); sound.hit();
            this.stepT = Math.min(this.stepT, this.stepInterval());
            if (!this.alive().length) { this.setState('clear'); }
            break;
          }
        }
        if (!hit && this.ufo) {
          const u = this.ufo;
          if (sh.x >= u.x && sh.x <= u.x + SPR.ufo.w && sh.y <= 24 + SPR.ufo.h && sh.y + 4 >= 24) {
            hit = true; this.addScore(u.points); this.floats.push({ x: u.x + 8, y: 22, text: String(u.points), color: COLOR.orange, t: 1200 }); this.burst(u.x + 8, 27, COLOR.orange, 12); this.ufo = null; sound.bonus();
          }
        }
        if (!hit) for (let k = 0; k < this.bullets.length; k++) { const b = this.bullets[k]; if (sh.x >= b.x - 1 && sh.x <= b.x + 3 && sh.y <= b.y + 7 && sh.y + 4 >= b.y) { this.bullets.splice(k, 1); hit = true; this.burst(sh.x, sh.y, COLOR.fg, 4); break; } }
        if (hit) this.shot = null;
      }

      /* Gegnergeschosse */
      const bs = this.boltSpeed();
      for (let k = this.bullets.length - 1; k >= 0; k--) {
        const b = this.bullets[k]; b.y += bs * s; b.f = (this.t / 90 | 0) & 1;
        let gone = b.y > H - 6;
        if (!gone) for (const bk of this.bunkers) { if (bk.solid(b.x + 1, b.y + 7)) { bk.damage(b.x + 1, b.y + 7, 2); gone = true; break; } }
        if (!gone && b.y + 7 >= PLAYER_Y && b.y <= PLAYER_Y + 8 && b.x + 3 >= p.x && b.x <= p.x + SPR.player.w) { this.bullets.splice(k, 1); this.killPlayer(false); return; }
        if (gone) this.bullets.splice(k, 1);
      }

      /* UFO */
      if (this.ufo) {
        const u = this.ufo; u.x += u.dir * 42 * s; u.snd -= dt;
        if (u.snd <= 0) { u.snd = 420; sound.ufo(); }
        if (u.x < -20 || u.x > W + 4) this.ufo = null;
      } else {
        this.ufoT -= dt;
        if (this.ufoT <= 0) {
          this.ufoT = rand(16000, 26000);
          if (this.alive().length >= 8) { const dir = Math.random() < 0.5 ? 1 : -1; this.ufo = { x: dir > 0 ? -16 : W, dir, points: UFO_POINTS[Math.floor(Math.random() * UFO_POINTS.length)], snd: 0 }; }
        }
      }
    }

    killPlayer(invaded) {
      this.burst(this.player.x + 6, PLAYER_Y + 4, COLOR.orange, 18);
      sound.die();
      if (invaded) { this.lives = 1; }
      this.setState('dying');
    }

    /* --- Zeichnen --- */
    render() {
      const g = this.g;
      g.fillStyle = COLOR.bg; g.fillRect(0, 0, W, H);
      if (this.state === 'attract') this.renderAttract(g);
      else this.renderPlay(g);
      const c = this.ctx;
      c.imageSmoothingEnabled = false;
      c.drawImage(this.buf, 0, 0, canvas.width, canvas.height);
    }
    renderHud(g) {
      text(g, 'SCORE', 8, 6, COLOR.dim); text(g, pad(this.score), 8, 16, COLOR.fg);
      text(g, 'HI', W / 2, 6, COLOR.dim, 1, 'center'); text(g, pad(this.hi), W / 2, 16, COLOR.orange, 1, 'center');
      text(g, 'WELLE', W - 8, 6, COLOR.dim, 1, 'right'); text(g, pad(this.wave, 2), W - 8, 16, COLOR.fg, 1, 'right');
      g.fillStyle = 'rgba(253,124,43,.55)'; g.fillRect(8, H - 12, W - 16, 1);
      for (let i = 0; i < Math.min(this.lives - 1, 4); i++) draw(g, SPR.life, 8 + i * 12, H - 9, COLOR.fg);
      text(g, 'CREDIT 01', W - 8, H - 9, COLOR.dim, 1, 'right');
    }
    renderPlay(g) {
      this.renderHud(g);
      this.bunkers.forEach((b) => b.render(g));
      const blink = (this.t / 120 | 0) & 1;
      this.inv.forEach((i) => { if (i.alive) draw(g, INV[i.t][this.frame], this.invX(i), this.invY(i), COLOR.fg); });
      this.booms.forEach((b) => draw(g, SPR.boom, b.x, b.y, COLOR.orange));
      if (this.ufo) draw(g, SPR.ufo, this.ufo.x, 24, COLOR.orange);
      if (this.state !== 'dying' && this.state !== 'over') { draw(g, SPR.player, this.player.x, PLAYER_Y, COLOR.fg); draw(g, SPR.playerDot, this.player.x, PLAYER_Y + 4, COLOR.orange); }
      else if (this.state === 'dying' && blink) { draw(g, SPR.player, this.player.x, PLAYER_Y, COLOR.orange); }
      if (this.shot) { draw(g, SPR.shot, this.shot.x, this.shot.y, COLOR.orange); g.fillStyle = 'rgba(253,124,43,.35)'; g.fillRect(this.shot.x - 1, this.shot.y + 1, 3, 3); }
      this.bullets.forEach((b) => draw(g, SPR.bolt[b.f], b.x, b.y, COLOR.fg));
      this.particles.forEach((p) => { g.fillStyle = p.color; g.fillRect(Math.round(p.x), Math.round(p.y), 1, 1); });
      this.floats.forEach((f) => text(g, f.text, f.x, f.y, f.color, 1, 'center'));
      if (this.state === 'ready') { text(g, this.readyText || 'BEREIT?', W / 2, 118, COLOR.orange, 2, 'center'); }
      if (this.state === 'clear') { text(g, 'WELLE ' + this.wave, W / 2, 110, COLOR.fg, 2, 'center'); text(g, 'GESCHAFFT', W / 2, 130, COLOR.orange, 1, 'center'); }
      if (this.state === 'over') { text(g, 'GAME OVER', W / 2, 96, COLOR.orange, 2, 'center'); }
      if (this.state === 'paused') { text(g, 'PAUSE', W / 2, 118, COLOR.fg, 2, 'center'); }
    }
    renderAttract(g) {
      const blink = (this.t / 500 | 0) & 1;
      text(g, 'SPOT', W / 2, 30, COLOR.fg, 3, 'center');
      text(g, 'INVADERS', W / 2, 56, COLOR.orange, 2, 'center');
      text(g, 'HI ' + pad(this.hi), W / 2, 80, COLOR.dim, 1, 'center');
      const rows = [[SPR.ufo, '=? MYSTERY', COLOR.orange], [SPR.squid[0], '=30 PUNKTE', COLOR.fg], [SPR.crab[0], '=20 PUNKTE', COLOR.fg], [SPR.octo[0], '=10 PUNKTE', COLOR.fg]];
      rows.forEach((r, k) => { const y = 104 + k * 16; draw(g, r[0], 72 - r[0].w, y, r[2]); text(g, r[1], 82, y + 1, COLOR.fg); });
      const t = INV[1][this.demo.frame];
      for (let k = 0; k < 5; k++) draw(g, t, this.demo.x + k * 16, 178, COLOR.dim);
      if (blink) text(g, 'INSERT COIN', W / 2, 206, COLOR.orange, 1, 'center');
      text(g, '1 SPIEL = 1 CODE', W / 2, 222, COLOR.dim, 1, 'center');
      draw(g, SPR.player, W / 2 - 6, H - 22, COLOR.fg); draw(g, SPR.playerDot, W / 2 - 6, H - 18, COLOR.orange);
    }
  }

  const game = new Game();
  ui.renderScores(-1);

  /* ---------- Skalierung: bevorzugt ganzzahlig auf Gerätepixel (scharf); wenn das mehr als ein Fünftel
     der Breite verschenken würde (Handys, schmale Tablets), wird die Röhre gebrochen skaliert, damit sie füllt ---------- */
  const screen = canvas.parentElement, bezel = screen.parentElement;
  function fit() {
    const dpr = window.devicePixelRatio || 1, cs = getComputedStyle(bezel);
    const avail = Math.max(W, bezel.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight));
    const exact = avail * dpr / W;
    let scale = Math.max(1, Math.floor(exact));
    if (scale / exact < 0.8) scale = exact;
    const bw = Math.round(W * scale), bh = Math.round(H * scale);
    if (canvas.width !== bw || canvas.height !== bh) { canvas.width = bw; canvas.height = bh; }
    const cssW = bw / dpr, cssH = bh / dpr;
    canvas.style.width = cssW + 'px'; canvas.style.height = cssH + 'px';
    screen.style.width = cssW + 'px';
    game.render();
  }
  if ('ResizeObserver' in window) new ResizeObserver(fit).observe(bezel); else addEventListener('resize', fit);
  fit();
  window.OSN_ARCADE = { game, Scores };

  /* ---------- Direktsprung per Anker (/#arcade): nach dem Laden nachjustieren, Start-Knopf fokussieren ---------- */
  const jumpHere = () => {
    if (!section.id || location.hash !== '#' + section.id) return;
    setTimeout(() => {
      section.scrollIntoView({ block: 'start' });
      const s = $('[data-arcade-start]'); if (s && game.state === 'attract') s.focus({ preventScroll: true });
    }, 80);
  };
  if (document.readyState === 'complete') jumpHere(); else addEventListener('load', jumpHere, { once: true });
  addEventListener('hashchange', jumpHere);

  /* ---------- Schleife (läuft nur, solange der Automat im Bild ist) ---------- */
  let running = false, raf = 0, last = 0, acc = 0;
  function frame(ts) {
    if (!running) return;
    const dt = Math.min(100, last ? ts - last : STEP); last = ts; acc += dt;
    while (acc >= STEP) { game.update(STEP); acc -= STEP; }
    game.render();
    syncState();
    raf = requestAnimationFrame(frame);
  }
  function run() { if (running) return; running = true; last = 0; acc = 0; syncState(); raf = requestAnimationFrame(frame); }
  function stop() { running = false; cancelAnimationFrame(raf); }
  if ('IntersectionObserver' in window) new IntersectionObserver((en) => { if (en.some((e) => e.isIntersecting)) run(); else { game.pause(); stop(); syncState(); } }, { threshold: 0.05 }).observe(cab);
  else run();
  document.addEventListener('visibilitychange', () => { if (document.hidden) game.pause(); });

  /* ---------- Tastatur ---------- */
  const KEYS = { ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right', Space: 'fire', ArrowUp: 'fire', KeyW: 'fire' };
  const inGame = () => game.state !== 'attract' && game.state !== 'over';
  document.addEventListener('keydown', (e) => {
    if (e.target && /^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(e.target.tagName) && e.target !== canvas) return;
    const focused = document.activeElement === canvas, k = KEYS[e.code];
    if (k && (inGame() || focused)) {
      input[k] = true; input.target = null; e.preventDefault();
      if (k === 'fire' && (game.state === 'attract' || game.state === 'over')) game.start();
      return;
    }
    if (e.code === 'Enter' && focused) { if (game.state === 'attract' || game.state === 'over') game.start(); else if (game.state === 'paused') game.resume(); e.preventDefault(); }
    if ((e.code === 'KeyP' || e.code === 'Escape') && inGame()) { game.togglePause(); e.preventDefault(); }
  });
  document.addEventListener('keyup', (e) => { const k = KEYS[e.code]; if (k) input[k] = false; });
  addEventListener('blur', () => { input.left = input.right = input.fire = false; });

  /* ---------- Tasten am Automaten (Touch und Maus) ---------- */
  $$('[data-arcade-btn]').forEach((btn) => {
    const key = btn.dataset.arcadeBtn;
    const down = (e) => { e.preventDefault(); btn.classList.add('is-down'); input.target = null; input[key] = true; if (key === 'fire' && (game.state === 'attract' || game.state === 'over')) game.start(); try { btn.setPointerCapture(e.pointerId); } catch (x) { /* egal */ } };
    const up = () => { btn.classList.remove('is-down'); input[key] = false; };
    btn.addEventListener('pointerdown', down);
    btn.addEventListener('pointerup', up); btn.addEventListener('pointercancel', up); btn.addEventListener('lostpointercapture', up);
    btn.addEventListener('contextmenu', (e) => e.preventDefault());
  });

  /* ---------- Finger/Maus direkt auf dem Bildschirm: ziehen bewegt, tippen feuert ---------- */
  const toGameX = (e) => { const r = canvas.getBoundingClientRect(); return clamp((e.clientX - r.left) / r.width * W, 0, W); };
  let dragging = false;
  canvas.addEventListener('pointerdown', (e) => {
    const idle = game.state === 'attract' || game.state === 'over' || game.state === 'paused';
    if (idle) {
      // Im Ruhezustand startet nur ein Mausklick. Ein Finger, der beim Scrollen den Bildschirm streift, darf nichts auslösen (dafür gibt es den Start-Knopf).
      if (e.pointerType !== 'mouse') return;
      e.preventDefault();
      if (game.state === 'paused') game.resume(); else game.start();
      return;
    }
    e.preventDefault();
    dragging = true; input.fire = true; input.target = toGameX(e);
    try { canvas.setPointerCapture(e.pointerId); } catch (x) { /* egal */ }
  });
  canvas.addEventListener('pointermove', (e) => { if (dragging) input.target = toGameX(e); });
  const endDrag = () => { if (!dragging) return; dragging = false; input.fire = false; input.target = null; };
  canvas.addEventListener('pointerup', endDrag); canvas.addEventListener('pointercancel', endDrag);
  /* ---------- Zustand ↔ Bedienelemente: im Ruhezustand darf über dem Bildschirm gescrollt werden, der Start-Knopf zeigt Start/Pause/Weiter ---------- */
  const goBtn = $('[data-arcade-go]'), goLabel = $('[data-arcade-go-label]');
  let lastState = null;
  function syncState() {
    if (game.state === lastState) return; lastState = game.state;
    const idle = game.state === 'attract' || game.state === 'over', paused = game.state === 'paused';
    canvas.style.touchAction = idle || paused ? 'pan-y' : 'none';
    cab.classList.toggle('is-idle', idle); cab.classList.toggle('is-paused', paused);
    if (goLabel) goLabel.textContent = idle ? T('arc_start', 'Start') : paused ? T('arc_resume', 'Weiter') : T('arc_pause', 'Pause');
    if (goBtn) goBtn.setAttribute('aria-label', idle ? T('arc_start_aria', 'Spiel starten') : paused ? T('arc_resume_aria', 'Spiel fortsetzen') : T('arc_pause_aria', 'Spiel pausieren'));
  }
  syncState();
  if (goBtn) goBtn.addEventListener('click', () => {
    if (game.state === 'paused') game.resume();
    else if (game.state === 'attract' || game.state === 'over') game.start();
    else game.pause();
  });

  /* ---------- Overlay-Knöpfe ---------- */
  const on = (sel, ev, fn) => { const el = $(sel); if (el) el.addEventListener(ev, fn); };
  on('[data-arcade-start]', 'click', () => game.start());
  on('[data-arcade-again]', 'click', () => game.start());
  on('[data-arcade-resume]', 'click', () => game.resume());
  on('[data-arcade-copy]', 'click', async () => {
    const code = ui.code ? ui.code.textContent.trim() : '';
    if (!code) return;
    let ok = false;
    try { await navigator.clipboard.writeText(code); ok = true; } catch (e) { ok = false; }
    if (!ok) { const r = document.createRange(); r.selectNodeContents(ui.code); const s = getSelection(); s.removeAllRanges(); s.addRange(r); try { ok = document.execCommand('copy'); } catch (e) { ok = false; } }
    const btn = ui.copy, old = btn.textContent; btn.textContent = ok ? T('arc_copied', 'Kopiert') : T('arc_selected', 'Markiert'); btn.classList.add('is-ok'); setTimeout(() => { btn.textContent = old; btn.classList.remove('is-ok'); }, 1600);
  });
  if (ui.form) ui.form.addEventListener('submit', (e) => {
    e.preventDefault();
    const name = (ui.initials.value || '').toUpperCase().replace(/[^A-Z0-9ÄÖÜ]/g, '').slice(0, 3) || 'AAA';
    const r = Scores.add(name, game.score, game.wave);
    game.hi = Scores.hi(); ui.renderScores(r.index); ui.form.hidden = true;
  });
})();
